import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for handling streaming image generation via SSE
 * Provides real-time partial images during AI generation
 */
export const useImageStreaming = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState(null);
  const [partialImage, setPartialImage] = useState(null);
  const [partialProgress, setPartialProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [finalImageUrl, setFinalImageUrl] = useState(null);
  
  const abortControllerRef = useRef(null);

  /**
   * Start streaming image generation
   * @param {string} recipeId - The recipe ID to generate image for
   * @param {string} baseUrl - The base API URL (optional, defaults to env)
   * @param {string} authToken - The authentication token
   * @returns {Promise<string|null>} - The final image URL or null on error
   */
  const startStreaming = useCallback(async (recipeId, authToken) => {
    // Reset state
    setIsStreaming(true);
    setStreamStatus('starting');
    setPartialImage(null);
    setPartialProgress({ current: 0, total: 0 });
    setError(null);
    setFinalImageUrl(null);

    // Create abort controller for cleanup
    abortControllerRef.current = new AbortController();

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${baseUrl}/admin/api/recipes/${recipeId}/generate-image/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let resultUrl = null;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let currentEventType = null;
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEventType) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              
              switch (currentEventType) {
                case 'status':
                  setStreamStatus(parsed.status || parsed.message);
                  break;
                
                case 'partial_image':
                  setPartialImage(`data:image/png;base64,${parsed.b64_json}`);
                  setPartialProgress({
                    current: parsed.partial_index,
                    total: parsed.total_partials,
                  });
                  setStreamStatus(`Generating image... (${parsed.partial_index}/${parsed.total_partials})`);
                  break;
                
                case 'completed':
                  resultUrl = parsed.image_url;
                  setFinalImageUrl(parsed.image_url);
                  setStreamStatus('completed');
                  break;
                
                case 'error':
                  setError(parsed.error);
                  setStreamStatus('error');
                  break;
                
                default:
                  // Ignore unknown event types
                  console.debug('Unknown SSE event type:', currentEventType);
                  break;
              }
            } catch (parseError) {
              console.error('Failed to parse SSE data:', parseError);
            }
            currentEventType = null;
          }
        }
      }

      return resultUrl;
    } catch (err) {
      if (err.name === 'AbortError') {
        setStreamStatus('cancelled');
        return null;
      }
      console.error('Streaming error:', err);
      setError(err.message);
      setStreamStatus('error');
      return null;
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setIsStreaming(false);
    setStreamStatus(null);
    setPartialImage(null);
    setPartialProgress({ current: 0, total: 0 });
    setError(null);
    setFinalImageUrl(null);
  }, []);

  return {
    // State
    isStreaming,
    streamStatus,
    partialImage,
    partialProgress,
    error,
    finalImageUrl,
    
    // Actions
    startStreaming,
    cancelStreaming,
    reset,
  };
};

export default useImageStreaming;

